import { Controller } from "@hotwired/stimulus"
import { filterAndSortRoles, escapeHtml } from "../utils/role_search"
import { bindClickOutside } from "../utils/click_outside"
import { handleListKeydown } from "../utils/keyboard_nav"

export default class extends Controller {
  static targets = ["input", "results", "hiddenFields", "selectedList", "countDisplay", "variantsData", "dataWarning"]

  connect() {
    this.isOpen = false
    this.highlightedIndex = -1
    this.filteredRoles = []
    this.roles = []
    this.selectedRoles = []

    if (this.hasVariantsDataTarget) {
      try {
        this.roles = JSON.parse(this.variantsDataTarget.textContent)
      } catch (e) {
        console.error("Failed to parse roles JSON:", e)
      }
    }

    this.unbindClickOutside = bindClickOutside(this.element, () => this.closeResults())
  }

  disconnect() {
    this.unbindClickOutside()
  }

  search() {
    const query = this.inputTarget.value.trim()

    if (query.length < 2) {
      this.closeResults()
      return
    }

    const selectedIds = new Set(this.selectedRoles.map(r => r.id))
    const available = this.roles.filter(r => !selectedIds.has(r.id))
    this.filteredRoles = filterAndSortRoles(available, query)
    this.renderResults()
    this.openResults()
  }

  renderResults() {
    if (this.filteredRoles.length === 0) {
      this.resultsTarget.innerHTML = `
        <div class="px-4 py-3 text-gray-500 text-sm">
          No matching roles found
        </div>
      `
      return
    }

    const html = this.filteredRoles.map((role, index) => {
      const isHighlighted = index === this.highlightedIndex
      const hasData = role.has_salary_data
      const textClass = hasData ? 'text-gray-900' : 'text-gray-400'
      const badge = hasData ? '' : '<span class="text-xs text-amber-600 ml-1">No data</span>'
      return `
        <div class="px-4 py-2 cursor-pointer ${isHighlighted ? 'bg-indigo-50' : 'hover:bg-gray-50'}"
             data-action="click->multi-role-autocomplete#select"
             data-index="${index}">
          <div class="font-medium text-sm ${textClass}">${escapeHtml(role.canonical_title)}${badge}</div>
          <div class="text-xs text-gray-500">${escapeHtml(role.career_family || '')}</div>
        </div>
      `
    }).join("")

    this.resultsTarget.innerHTML = html
  }

  openResults() {
    this.resultsTarget.classList.remove("hidden")
    this.isOpen = true
    this.highlightedIndex = -1
  }

  closeResults() {
    this.resultsTarget.classList.add("hidden")
    this.isOpen = false
    this.highlightedIndex = -1
  }

  select(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    const role = this.filteredRoles[index]
    if (role) this.addRole(role)
  }

  addRole(role) {
    if (this.selectedRoles.find(r => r.id === role.id)) return

    this.selectedRoles.push(role)
    this.inputTarget.value = ""
    this.closeResults()
    this.renderSelected()
  }

  removeRole(event) {
    const roleId = event.currentTarget.dataset.roleId
    this.selectedRoles = this.selectedRoles.filter(r => String(r.id) !== String(roleId))
    this.renderSelected()
  }

  renderSelected() {
    const chipsHtml = this.selectedRoles.map(role => {
      const hasData = role.has_salary_data
      const chipClasses = hasData
        ? 'bg-indigo-100 text-indigo-700'
        : 'bg-amber-50 text-amber-700 border border-amber-300'
      const btnClasses = hasData
        ? 'text-indigo-500 hover:text-indigo-700'
        : 'text-amber-500 hover:text-amber-700'
      return `
      <span class="inline-flex items-center gap-1 px-3 py-1 ${chipClasses} text-sm rounded-full">
        ${escapeHtml(role.canonical_title)}
        <button type="button"
                data-action="click->multi-role-autocomplete#removeRole"
                data-role-id="${role.id}"
                class="${btnClasses} font-bold ml-1">&times;</button>
      </span>
    `}).join("")
    this.selectedListTarget.innerHTML = chipsHtml

    const fieldsHtml = this.selectedRoles.map(role =>
      `<input type="hidden" name="job_role_ids[]" value="${role.id}" data-has-salary-data="${role.has_salary_data ? 'true' : 'false'}">`
    ).join("")
    this.hiddenFieldsTarget.innerHTML = fieldsHtml

    if (this.hasCountDisplayTarget) {
      const count = this.selectedRoles.length
      this.countDisplayTarget.textContent = count > 0 ? `${count} role${count !== 1 ? 's' : ''} selected` : ''
    }

    this.updateDataWarning()
  }

  updateDataWarning() {
    if (!this.hasDataWarningTarget) return

    const total = this.selectedRoles.length
    const withoutData = this.selectedRoles.filter(r => !r.has_salary_data).length

    if (total > 0 && withoutData > 0) {
      const withData = total - withoutData
      this.dataWarningTarget.textContent =
        `${withoutData} of ${total} selected role${total !== 1 ? 's' : ''} lack${withoutData === 1 ? 's' : ''} salary data \u2014 at least 3 roles with data are needed.`
      this.dataWarningTarget.classList.remove("hidden")
    } else {
      this.dataWarningTarget.textContent = ""
      this.dataWarningTarget.classList.add("hidden")
    }
  }

  get rolesWithDataCount() {
    return this.selectedRoles.filter(r => r.has_salary_data).length
  }

  handleKeydown(event) {
    if (!this.isOpen) return

    handleListKeydown(event, {
      highlightedIndex: this.highlightedIndex,
      itemCount: this.filteredRoles.length,
      onHighlight: (index) => { this.highlightedIndex = index; this.renderResults() },
      onSelect: (index) => this.addRole(this.filteredRoles[index]),
      onClose: () => this.closeResults()
    })
  }
}
